import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { Card, Input, Tag } from '@/shared/ui/primitives';
import EntityTable from '@/shared/ui/EntityTable';
import { usePermissions } from '../hooks/usePermissions';
import type { AdminPermissionRecord } from '../api/permission.api';

export default function PermissionsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data = [], isLoading } = usePermissions();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((permission) => [
      permission.key,
      permission.resource,
      permission.action,
      permission.description,
      formatPermissionLabel(permission.key),
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div>
      <PageHeader title={t('permissions.title')} subtitle={t('permissions.subtitle')} />

      <Card style={{ marginBottom: 16 }}>
        <div className="ds-row" style={{ gap: 12, alignItems: 'center' }}>
          <Input
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            placeholder={t('permissions.searchPlaceholder')}
            style={{ minWidth: 280 }}
          />
          <div className="ds-muted">{t('permissions.count', { count: rows.length })}</div>
        </div>
      </Card>

      <EntityTable<AdminPermissionRecord>
        data={rows}
        loading={isLoading}
        rowKey="id"
        page={0}
        pageSize={rows.length || 20}
        total={rows.length}
        onPageChange={() => undefined}
        columns={[
          {
            title: t('permissions.columns.permission'),
            render: (_: any, row) => (
              <div>
                <strong>{formatPermissionLabel(row.key)}</strong>
                <div className="ds-muted">{row.description || t('permissions.noDescription')}</div>
              </div>
            ),
          },
          {
            title: t('permissions.columns.area'),
            dataIndex: 'resource',
            render: (resource: string) => <Tag color="info">{formatPermissionLabel(resource || '')}</Tag>,
          },
          {
            title: t('permissions.columns.action'),
            dataIndex: 'action',
            render: (action: string) => <Tag>{formatPermissionLabel(action || '')}</Tag>,
          },
        ]}
      />
    </div>
  );
}

function formatPermissionLabel(value?: string | null) {
  if (!value) return '—';
  return value
    .replace(/^admin:/, '')
    .replace(/[_:.]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
